import { z } from "zod";
import { boundedConnectorConfigString, connectorOauthRefreshConfigShape, connectorConfigScopeSchema, connectorConfigTimestampSchema, connectorUpdatedAtConfigShape, delimitedConnectorConfigString, emptyConnectorConfigSchema, httpConnectorConfigUrl, uniqueConnectorConfigStrings, } from "./connector-config-primitives.js";
const oauthConnectionMetadataShape = {
    expiresAt: connectorConfigTimestampSchema.optional(),
    oauthConnectedAt: connectorConfigTimestampSchema.optional(),
    scope: connectorConfigScopeSchema.optional(),
    tokenType: boundedConnectorConfigString("tokenType", 128).optional(),
};
const xeroConfigShape = {
    tenantId: boundedConnectorConfigString("tenantId", 256),
    tenantName: boundedConnectorConfigString("tenantName", 512).optional(),
    tenantType: boundedConnectorConfigString("tenantType", 128).optional(),
    connectionId: boundedConnectorConfigString("connectionId", 256).optional(),
    ...oauthConnectionMetadataShape,
};
export const xeroConnectorConfigSchemas = {
    create: z.object(xeroConfigShape).strict(),
    update: emptyConnectorConfigSchema,
    persisted: z.object({
        ...xeroConfigShape,
        ...connectorUpdatedAtConfigShape,
        oauthReconnectRequiredAt: connectorConfigTimestampSchema.optional(),
    }).strict(),
};
const sunProxyShape = {
    proxyMode: z.enum(["worker", "server"]).optional(),
    workerUrl: httpConnectorConfigUrl("workerUrl").optional(),
    proxyUrl: httpConnectorConfigUrl("proxyUrl").optional(),
};
const sunConfigShape = {
    authUrl: httpConnectorConfigUrl("authUrl"),
    executeUrl: httpConnectorConfigUrl("executeUrl"),
    businessUnit: boundedConnectorConfigString("businessUnit", 128),
    budgetCodes: delimitedConnectorConfigString("budgetCodes", {
        maxItems: 100,
    }).optional(),
    currencyCode: z
        .string()
        .trim()
        .regex(/^[A-Z]{3}$/u, "currencyCode must be a three-letter uppercase code")
        .optional(),
    invoiceQueryGroup: boundedConnectorConfigString("invoiceQueryGroup", 256).optional(),
    purchaseInvoiceTransactionType: delimitedConnectorConfigString("purchaseInvoiceTransactionType", { maxItems: 100, separator: /[\n,]/u }).optional(),
    salesInvoiceTransactionType: delimitedConnectorConfigString("salesInvoiceTransactionType", { maxItems: 100, separator: /[\n,]/u }).optional(),
    ...sunProxyShape,
};
function validateSunConfig(value, ctx, patch) {
    const proxyTouched = ["proxyMode", "workerUrl", "proxyUrl"].some((key) => Object.hasOwn(value, key));
    if (!patch || proxyTouched) {
        if (value.proxyMode === "worker") {
            if (!value.workerUrl) {
                ctx.addIssue({
                    code: "custom",
                    path: ["workerUrl"],
                    message: "workerUrl is required when proxyMode is worker",
                });
            }
            if (value.proxyUrl) {
                ctx.addIssue({
                    code: "custom",
                    path: ["proxyUrl"],
                    message: "proxyUrl contradicts proxyMode worker",
                });
            }
        }
        else if (value.proxyMode === "server") {
            if (!value.proxyUrl) {
                ctx.addIssue({
                    code: "custom",
                    path: ["proxyUrl"],
                    message: "proxyUrl is required when proxyMode is server",
                });
            }
            if (value.workerUrl) {
                ctx.addIssue({
                    code: "custom",
                    path: ["workerUrl"],
                    message: "workerUrl contradicts proxyMode server",
                });
            }
        }
        else if (value.workerUrl || value.proxyUrl) {
            ctx.addIssue({
                code: "custom",
                path: ["proxyMode"],
                message: "proxyMode is required when a proxy URL is configured",
            });
        }
    }
    const invoiceTypeTouched = [
        "purchaseInvoiceTransactionType",
        "salesInvoiceTransactionType",
    ].some((key) => Object.hasOwn(value, key));
    if (invoiceTypeTouched && !value.invoiceQueryGroup) {
        ctx.addIssue({
            code: "custom",
            path: ["invoiceQueryGroup"],
            message: "invoiceQueryGroup is required with invoice transaction types",
        });
    }
}
const sunCreateSchema = z.object(sunConfigShape).strict().superRefine((value, ctx) => {
    validateSunConfig(value, ctx, false);
});
// Proxy mode changes require deleting stale mutually-exclusive URL keys, which
// JSONB shallow-merge patches cannot do. Operators use a full upsert instead.
const sunUpdateSchema = z.object(sunConfigShape).pick({
    authUrl: true,
    executeUrl: true,
    businessUnit: true,
    budgetCodes: true,
    currencyCode: true,
    invoiceQueryGroup: true,
    purchaseInvoiceTransactionType: true,
    salesInvoiceTransactionType: true,
}).partial().strict().superRefine((value, ctx) => {
    validateSunConfig(value, ctx, true);
});
const sunPersistedSchema = z.object({
    ...sunConfigShape,
}).strict().superRefine((value, ctx) => {
    validateSunConfig(value, ctx, false);
});
export const sunConnectorConfigSchemas = {
    create: sunCreateSchema,
    update: sunUpdateSchema,
    persisted: sunPersistedSchema,
};
const overtureDocumentTemplateSchema = boundedConnectorConfigString("documentDownloadEndpointTemplate", 2_048).superRefine((value, ctx) => {
    if (!["{id}", "{externalId}", "{filename}"].some((token) => value.includes(token))) {
        ctx.addIssue({
            code: "custom",
            message: "documentDownloadEndpointTemplate must contain {id}, {externalId}, or {filename}",
        });
    }
    if (/^[^/]+:/u.test(value) && !/^https?:\/\//u.test(value)) {
        ctx.addIssue({
            code: "custom",
            message: "documentDownloadEndpointTemplate must be a relative path or HTTP(S) URL",
        });
    }
    if (value.startsWith("//") || /[\u0000\r\n]/u.test(value)) {
        ctx.addIssue({
            code: "custom",
            message: "documentDownloadEndpointTemplate contains an unsafe path form",
        });
    }
    if (/[?#]/u.test(value)) {
        ctx.addIssue({
            code: "custom",
            message: "documentDownloadEndpointTemplate must not contain a query string or fragment",
        });
    }
});
const overtureConfigShape = {
    baseUrl: httpConnectorConfigUrl("baseUrl"),
    tokenPathOverride: z
        .string()
        .trim()
        .min(1)
        .max(1_024)
        .regex(/^\/[^?#]*$/u, "tokenPathOverride must be an absolute path without query or fragment")
        .optional(),
    userAgent: boundedConnectorConfigString("userAgent", 512)
        .refine((value) => !/[\u0000-\u001f\u007f]/u.test(value), "userAgent contains control characters")
        .optional(),
    documentDownloadEndpointTemplate: overtureDocumentTemplateSchema.optional(),
};
function validateOvertureTemplate(value, ctx, patch) {
    const template = value.documentDownloadEndpointTemplate;
    if (!template || !/^https?:\/\//u.test(template))
        return;
    if (patch || !value.baseUrl) {
        ctx.addIssue({
            code: "custom",
            path: ["documentDownloadEndpointTemplate"],
            message: "an absolute document template requires a full connector upsert",
        });
        return;
    }
    let templateUrl;
    let baseUrl;
    try {
        templateUrl = new URL(template);
        baseUrl = new URL(value.baseUrl);
    }
    catch {
        ctx.addIssue({
            code: "custom",
            path: ["documentDownloadEndpointTemplate"],
            message: "an absolute document template must be a valid HTTP(S) URL",
        });
        return;
    }
    if (templateUrl.origin !== baseUrl.origin ||
        templateUrl.username ||
        templateUrl.password ||
        templateUrl.search ||
        templateUrl.hash) {
        ctx.addIssue({
            code: "custom",
            path: ["documentDownloadEndpointTemplate"],
            message: "an absolute document template must share the connector baseUrl origin and contain no credentials or fragment",
        });
    }
}
export const overtureConnectorConfigSchemas = {
    create: z.object(overtureConfigShape).strict().superRefine((value, ctx) => {
        validateOvertureTemplate(value, ctx, false);
    }),
    update: z.object(overtureConfigShape).pick({
        tokenPathOverride: true,
        userAgent: true,
        documentDownloadEndpointTemplate: true,
    }).partial().strict().superRefine((value, ctx) => {
        validateOvertureTemplate(value, ctx, true);
    }),
    persisted: z.object({
        ...overtureConfigShape,
    }).strict().superRefine((value, ctx) => {
        validateOvertureTemplate(value, ctx, false);
    }),
};
const stripeConfigShape = {
    stripeUserId: boundedConnectorConfigString("stripeUserId", 256),
    stripeAuthMode: z.enum(["oauth", "app_developer_key"]),
    livemode: z.boolean(),
    ...oauthConnectionMetadataShape,
};
export const stripeConnectorConfigSchemas = {
    create: z.object(stripeConfigShape).strict(),
    update: emptyConnectorConfigSchema,
    persisted: z.object({
        ...stripeConfigShape,
        ...connectorOauthRefreshConfigShape,
        disconnectedAt: connectorConfigTimestampSchema.optional(),
    }).strict(),
};
const goCardlessMutableShape = {
    accountIds: uniqueConnectorConfigStrings("accountIds", 1_000).optional(),
    requisitionId: boundedConnectorConfigString("requisitionId", 256).optional(),
};
const goCardlessConfigShape = {
    ...goCardlessMutableShape,
    accessTokenExpiresAt: connectorConfigTimestampSchema.optional(),
};
export const goCardlessConnectorConfigSchemas = {
    create: z.object(goCardlessConfigShape).strict(),
    update: z.object(goCardlessMutableShape).strict(),
    persisted: z.object({
        ...goCardlessConfigShape,
        ...connectorUpdatedAtConfigShape,
    }).strict(),
};
const sharePointSiteIdsSchema = uniqueConnectorConfigStrings("selectedSiteIds", 5_000);
const sharePointConnectionShape = {
    ...oauthConnectionMetadataShape,
    microsoftUserEmail: z.string().trim().email().max(320).optional(),
    microsoftUserId: boundedConnectorConfigString("microsoftUserId", 256).optional(),
    microsoftUserName: boundedConnectorConfigString("microsoftUserName", 512).optional(),
    selectedSiteIds: sharePointSiteIdsSchema.optional(),
};
const sharePointAvailableSiteSchema = z.object({
    id: boundedConnectorConfigString("availableSites.id", 1_024),
    name: boundedConnectorConfigString("availableSites.name", 1_024),
    webUrl: httpConnectorConfigUrl("availableSites.webUrl", { allowQuery: true })
        .nullable()
        .optional(),
}).strict();
export const sharePointConnectorConfigSchemas = {
    create: z.object(sharePointConnectionShape).strict(),
    update: z.object({ selectedSiteIds: sharePointSiteIdsSchema }).partial().strict(),
    persisted: z.object({
        ...sharePointConnectionShape,
        availableSites: z.array(sharePointAvailableSiteSchema).max(5_000).optional(),
        sitesDiscoveredAt: connectorConfigTimestampSchema.optional(),
        disconnectCleanupRequestedAt: connectorConfigTimestampSchema.optional(),
        ...connectorUpdatedAtConfigShape,
    }).strict(),
};
const hubSpotConfigShape = {
    portalId: z.string().trim().regex(/^\d+$/u, "portalId must contain digits only").max(32),
    hubDomain: z
        .string()
        .trim()
        .min(1)
        .max(253)
        .regex(/^(?=.{1,253}$)(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)*[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/u, "hubDomain must be a hostname")
        .optional(),
    ...oauthConnectionMetadataShape,
};
export const hubSpotConnectorConfigSchemas = {
    create: z.object(hubSpotConfigShape).strict(),
    update: emptyConnectorConfigSchema,
    persisted: z.object({
        ...hubSpotConfigShape,
        ...connectorUpdatedAtConfigShape,
    }).strict(),
};
/**
 * The Twin as a source carries no configuration: the runner reads confirmed
 * identity links from the tenant's own database.
 */
const twinConfigSchema = z.object({}).strict();
export const twinConnectorConfigSchemas = {
    create: twinConfigSchema,
    update: twinConfigSchema,
    persisted: twinConfigSchema,
};
